import type { PreDrillWorkspaceListRow } from 'types/api-responses';

// One selectable permit in the Create modal's Permit dropdown.
export interface PreDrillPermitOption {
  /** Permit header id — the workspace is created against this. */
  id: number;
  /** Permit UWI14 — keys the workspace + shown in the dropdown. */
  uwi14: string;
}

export function permitLabel(
  permit: PreDrillPermitOption | null | undefined
): string {
  return permit ? `${permit.uwi14} – Permit` : '';
}

type ApiFieldError = { code?: string; message?: string };

// Walk the create-workspace error payload (several shapes) and collect
// human-readable message strings, tolerating non-strings.
export function collectErrorMessages(value: unknown): string[] {
  if (typeof value === 'string') {
    return value.trim().length > 0 ? [value] : [];
  }
  if (Array.isArray(value)) {
    return value.flatMap(collectErrorMessages);
  }
  if (value && typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    if (typeof obj.message === 'string') {
      return collectErrorMessages(obj.message);
    }
    return Object.values(obj).flatMap(collectErrorMessages);
  }
  return [];
}

// Field-level name error (if any), from the API array-of-objects (code 'NAME')
// or the legacy field-keyed shape.
export function findNameError(payload: unknown): string | null {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return null;
  }
  const node = (payload as { errors?: unknown }).errors;
  if (Array.isArray(node)) {
    const hit = node.find(
      (e) => (e as ApiFieldError)?.code?.toUpperCase() === 'NAME'
    ) as ApiFieldError | undefined;
    return hit?.message ?? null;
  }
  if (node && typeof node === 'object') {
    const msg = (node as Record<string, unknown>).name;
    return Array.isArray(msg) && msg.length > 0 ? String(msg[0]) : null;
  }
  return null;
}

// Default workspace pick: the one flagged `isDefault`, else the most recently
// created; null for an empty list.
export function pickDefaultWorkspace(
  rows: PreDrillWorkspaceListRow[]
): PreDrillWorkspaceListRow | null {
  if (rows.length === 0) return null;
  const flagged = rows.find((r) => r.isDefault);
  if (flagged) return flagged;
  return rows.reduce((latest, r) => {
    return (r.createdAt ?? '') > (latest.createdAt ?? '') ? r : latest;
  });
}

export function verdictLabel(verdict: string | null): string {
  if (verdict === 'correct') return 'Correct';
  if (verdict === 'incorrect') return 'Incorrect';
  return '--';
}

// Lifecycle status badge (Draft / Generating / Confirmed / Promoted) from the
// promoted / geometry / target-zone flags. Promoted and Draft carry `lifecycle`, so the drawer
// draws them with LifecycleStatusTag and they match every other Promoted and Draft tag.
export function workspaceStatus(
  row: PreDrillWorkspaceListRow
):
  | { label: string; lifecycle: 'promoted' | 'draft' }
  | { label: string; type: 'purple' | 'teal' } {
  if (row.promoted) return { label: 'Promoted', lifecycle: 'promoted' };
  if (row.geometryStatus === 'processing')
    return { label: 'Generating', type: 'purple' };
  if (row.targetZoneStatus === 'confirmed')
    return { label: 'Confirmed', type: 'teal' };
  return { label: 'Draft', lifecycle: 'draft' };
}

// Pull a human message from the default-selection error body; fall back to a
// plain string or a generic message.
export function selectErrorText(body: unknown): string {
  const errors = (body as { errors?: { message?: string }[] } | null)?.errors;
  if (Array.isArray(errors)) {
    const msgs = errors
      .map((e) => e?.message)
      .filter((m): m is string => typeof m === 'string' && m.trim().length > 0);
    if (msgs.length > 0) return msgs.join(' ');
  }
  if (typeof body === 'string' && body.trim().length > 0) return body;
  return 'Unable to set the default source. Please try again.';
}
